import { OAuthStorage } from 'angular-oauth2-oidc';

/**
 * Storage for angular-oauth2-oidc.
 *
 * Tokens (access/refresh/id) live in localStorage so every tab of this origin shares a
 * session and a reload does not force a fresh AS round-trip. Cross-tab refresh is still
 * coordinated by CrossTabTokenSync (one leader redeems the rotating refresh token).
 *
 * Transient PKCE material (`nonce`, `PKCE_verifier`) is dual-written:
 *
 * 1. sessionStorage — per tab, survives the SPA→AS→SPA round-trip in that same tab.
 * 2. localStorage, scoped per flow (`qlm-oidc-flow:<nonce>:…`) — so Arc-style browsers
 *    that open the OIDC callback in a *different* tab than the one that started the
 *    flow can still finish the exchange (sessionStorage would be empty there).
 *
 * On a callback, reads prefer the URL’s flow (scoped by `state` nonce) over sessionStorage.
 * sessionStorage can hold a newer same-tab flow that was stamped just before a redirect
 * (retryOnce) or otherwise diverge from the code in the address bar; handing the token
 * endpoint that verifier yields a bare `invalid_grant`.
 *
 * Fixed localStorage keys for nonce/PKCE are never written (legacy values are cleared on
 * remove). They are a single shared slot and are what let the email tab clobber the
 * waiting tab.
 */

/** Keys whose value belongs to one specific authorization request. */
const FLOW_KEYS = ['nonce', 'PKCE_verifier'];
const FLOW_PREFIX = 'qlm-oidc-flow:';
/** Must match AuthConfig.nonceStateSeparator (library default, not overridden in configure). */
const NONCE_STATE_SEPARATOR = ';';
/** Abandoned flows are swept after this long; comfortably longer than any real login. */
const FLOW_TTL_MS = 15 * 60 * 1000;

export class LocalStorageOAuthStorage extends OAuthStorage {
  /**
   * Flow this page load is the callback for, captured at construction.
   *
   * MUST be read this early: tryLoginCodeFlow strips `code` and `state` from the address
   * bar (history.replaceState) BEFORE it validates the nonce or exchanges the code, so by
   * the time any read reaches us the URL no longer identifies the flow. This service is
   * constructed during app bootstrap, well before the callback component runs tryLogin.
   */
  private readonly callbackFlowId: string | null = this.readFlowIdFromUrl();

  /**
   * Nonce of the flow this tab most recently started. In memory, so a concurrent tab
   * writing its own nonce cannot cause us to tag our verifier with its flow.
   */
  private currentFlowId: string | null = null;

  getItem(key: string): string | null {
    if (FLOW_KEYS.includes(key)) {
      // Callback: the code in the URL belongs to callbackFlowId. Prefer that flow's
      // scoped material so a polluted sessionStorage cannot supply the wrong verifier.
      if (this.callbackFlowId) {
        const scoped = localStorage.getItem(this.scopedKey(this.callbackFlowId, key));
        if (scoped !== null) {
          return scoped;
        }
        // Session only when it still belongs to this callback's flow.
        const sessionNonce = sessionStorage.getItem('nonce');
        if (sessionNonce === this.callbackFlowId) {
          const fromSession = sessionStorage.getItem(key);
          if (fromSession !== null) {
            return fromSession;
          }
        }
        // Fixed keys are unsafe on callback (rival tab overwrite).
        return null;
      }

      const fromSession = sessionStorage.getItem(key);
      if (fromSession !== null) {
        return fromSession;
      }
      if (this.currentFlowId) {
        const scoped = localStorage.getItem(this.scopedKey(this.currentFlowId, key));
        if (scoped !== null) {
          return scoped;
        }
      }
    }
    return localStorage.getItem(key);
  }

  setItem(key: string, data: string): void {
    if (key === 'nonce') {
      // First write of a new flow: adopt it and drop any long-abandoned ones.
      this.currentFlowId = data;
      localStorage.setItem(this.scopedKey(data, 'ts'), String(Date.now()));
      this.sweepExpiredFlows();
    }
    if (FLOW_KEYS.includes(key)) {
      try {
        sessionStorage.setItem(key, data);
      } catch {
        // Private mode / quota: scoped localStorage below is enough for callback recovery.
      }
      if (this.currentFlowId) {
        localStorage.setItem(this.scopedKey(this.currentFlowId, key), data);
      }
      // Deliberately do NOT write nonce/PKCE to the library's fixed localStorage keys.
      // Those are a shared single slot — the email tab overwriting them is what produced
      // invalid_grant / wrong-nonce on the waiting tab. Session + scoped-by-flow is enough.
      return;
    }
    localStorage.setItem(key, data);
  }

  removeItem(key: string): void {
    if (FLOW_KEYS.includes(key)) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // ignore
      }
      // Only the key being removed -- NOT the whole flow. The library may drop
      // PKCE_verifier and then read `nonce` again to validate the id_token's nonce claim
      // (processIdToken). Discarding the flow here left that second read falling back to
      // the fixed key, which a rival tab may have overwritten ("Wrong nonce").
      const flowId = this.flowId();
      if (flowId) {
        localStorage.removeItem(this.scopedKey(flowId, key));
      }
      // Clear any legacy fixed-key value from older builds.
      localStorage.removeItem(key);
      return;
    }
    localStorage.removeItem(key);
  }

  /**
   * Flow this read belongs to: the captured callback flow when finishing a login,
   * otherwise the flow this tab most recently started.
   */
  private flowId(): string | null {
    return this.callbackFlowId ?? this.currentFlowId;
  }

  /**
   * Nonce of the flow this page load is the callback for, from the `state` query parameter.
   * Null when not on a callback (no `state`), which is when the fixed keys are correct.
   */
  private readFlowIdFromUrl(): string | null {
    try {
      const state = new URLSearchParams(window.location.search).get('state');
      if (!state) {
        return null;
      }
      const separator = state.indexOf(NONCE_STATE_SEPARATOR);
      return separator > -1 ? state.substring(0, separator) : state;
    } catch {
      return null;
    }
  }

  private scopedKey(flowId: string, key: string): string {
    return `${FLOW_PREFIX}${flowId}:${key}`;
  }

  private forgetFlow(flowId: string): void {
    localStorage.removeItem(this.scopedKey(flowId, 'ts'));
    for (const key of FLOW_KEYS) {
      localStorage.removeItem(this.scopedKey(flowId, key));
    }
  }

  /**
   * Removes flows older than FLOW_TTL_MS. Abandoned flows (user closed the AS page) would
   * otherwise accumulate forever. Deletion-only, so two tabs sweeping at once is harmless --
   * unlike a read-modify-write of a shared record, which could lose a live flow.
   */
  private sweepExpiredFlows(): void {
    const cutoff = Date.now() - FLOW_TTL_MS;
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(FLOW_PREFIX) || !key.endsWith(':ts')) {
        continue;
      }
      const startedAt = Number(localStorage.getItem(key));
      if (!startedAt || startedAt < cutoff) {
        stale.push(key.slice(FLOW_PREFIX.length, -':ts'.length));
      }
    }
    stale.forEach((flowId) => this.forgetFlow(flowId));
  }
}
